// Moderation Dashboard Component
// Allows moderators to review and approve/reject community notes
// and manage reported class issues

import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { IssuesManagement } from './IssuesManagement';
import { logger } from '../utils/logger';
import '../styles/ModerationDashboard.css';

interface CommunityNote {
  id: string;
  class_name: string;
  method_name?: string;
  property_name?: string;
  content: string;
  author_id: string;
  author_username: string;
  author_avatar: string;
  status: 'pending' | 'approved' | 'rejected';
  created_at: string;
  updated_at: string;
}

export const ModerationDashboard: React.FC = () => {
  const { user, isAuthenticated, getAuthToken } = useAuth();
  const [activeTab, setActiveTab] = useState<'notes' | 'issues'>('notes');
  const [notes, setNotes] = useState<CommunityNote[]>([]);
  const [allNotes, setAllNotes] = useState<CommunityNote[]>([]);
  const [loading, setLoading] = useState(true);
  const [notesFilter, setNotesFilter] = useState<'pending' | 'approved' | 'rejected'>('pending');
  const [selectedNote, setSelectedNote] = useState<CommunityNote | null>(null);
  const [rejectionReason, setRejectionReason] = useState('');
  const [moderating, setModerating] = useState(false);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    if (isAuthenticated && user?.is_moderator) {
      if (activeTab === 'notes') {
        fetchNotes();
      }
    }
  }, [isAuthenticated, user, notesFilter, activeTab]);

  const fetchNotes = async () => {
    setLoading(true);
    try {
      const token = getAuthToken();

      // Fetch all notes for counts
      const allNotesPromises = ['pending', 'approved', 'rejected'].map(status =>
        fetch(`/api/notes/pending?status=${status}`, {
          headers: { 'Authorization': `Bearer ${token}` }
        }).then(r => r.json())
      );

      const allNotesResults = await Promise.all(allNotesPromises);
      const allNotesCombined = allNotesResults.flatMap(result => result.notes || []);
      setAllNotes(allNotesCombined);

      // Fetch current filter
      const response = await fetch(`/api/notes/pending?status=${notesFilter}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      const data = await response.json();
      setNotes(data.notes || []);
    } catch (error) {
      logger.error('Failed to fetch notes:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleModerateNote = async (noteId: string, action: 'approve' | 'reject', reason?: string) => {
    setModerating(true);
    try {
      const token = getAuthToken();
      const response = await fetch('/api/notes/moderate', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          note_id: noteId,
          action,
          rejection_reason: action === 'reject' ? reason : undefined
        })
      });

      if (response.ok) {
        setSelectedNote(null);
        setRejectionReason('');
        fetchNotes(); // Refresh list
      }
    } catch (error) {
      logger.error('Note moderation failed:', error);
    } finally {
      setModerating(false);
    }
  };

  const handleDeleteNote = async (noteId: string) => {
    if (!confirm('Are you sure you want to delete this note? This action cannot be undone.')) {
      return;
    }

    setDeleting(true);
    try {
      const token = getAuthToken();
      const response = await fetch(`/api/notes/delete/${noteId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        setSelectedNote(null);
        fetchNotes(); // Refresh list
      } else {
        const data = await response.json();
        alert(`Delete failed: ${data.error}`);
      }
    } catch (error) {
      logger.error('Delete failed:', error);
      alert('Delete failed. Please try again.');
    } finally {
      setDeleting(false);
    }
  };

  if (!isAuthenticated || !user?.is_moderator) {
    return (
      <div className="moderation-access-denied">
        <h2>Access Denied</h2>
        <p>You must be a moderator to access this page.</p>
      </div>
    );
  }

  return (
    <div className="moderation-dashboard">
      <h1>Moderation Dashboard</h1>

      {/* Main Tabs */}
      <div className="moderation-tabs">
        <button
          onClick={() => setActiveTab('notes')}
          className={`moderation-tab ${activeTab === 'notes' ? 'active' : ''}`}
        >
          📝 Community Notes
        </button>
        <button
          onClick={() => setActiveTab('issues')}
          className={`moderation-tab ${activeTab === 'issues' ? 'active' : ''}`}
        >
          🐛 Reported Issues
        </button>
      </div>

      {/* Render active tab content */}
      {activeTab === 'issues' ? (
        <IssuesManagement />
      ) : (
        /* Notes Moderation */
        <>
          {/* Filter Tabs for Notes */}
          <div className="filter-tabs">
            {(['pending', 'approved', 'rejected'] as const).map((status) => (
              <button
                key={status}
                onClick={() => setNotesFilter(status)}
                className={`filter-tab ${notesFilter === status ? 'active' : ''}`}
                style={{ textTransform: 'capitalize' }}
              >
                {status} ({allNotes.filter(n => n.status === status).length})
              </button>
            ))}
          </div>

          {loading ? (
            <div className="moderation-loading">Loading...</div>
          ) : notes.length === 0 ? (
            <div className="moderation-empty">
              No {notesFilter} notes found.
            </div>
          ) : (
            <div className="examples-grid">
              {notes.map((note) => {
                const noteTarget = note.method_name
                  ? `${note.class_name}::${note.method_name}()`
                  : note.property_name
                  ? `${note.class_name}.${note.property_name}`
                  : note.class_name;

                return (
                  <div key={note.id} className="example-card" style={{ padding: '0.5rem 0.75rem' }}>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '0.75rem', flexWrap: 'wrap' }}>
                      {/* Left: Title and metadata */}
                      <div style={{ flex: '1 1 200px', minWidth: '180px' }}>
                        <h3 style={{ margin: '0 0 0.15rem 0', fontSize: '14px', fontWeight: '600' }}>
                          {noteTarget}
                        </h3>
                        <div style={{ fontSize: '11px', color: 'var(--ifm-color-emphasis-600)' }}>
                          by {note.author_username} · {new Date(note.created_at).toLocaleDateString()}
                        </div>
                        {note.status !== 'pending' && (
                          <div style={{ display: 'flex', gap: '0.25rem', marginTop: '0.15rem' }}>
                            <span style={{
                              fontSize: '10px',
                              padding: '0.1rem 0.35rem',
                              borderRadius: '3px',
                              backgroundColor: note.status === 'approved' ? '#10b981' : '#ef4444',
                              color: 'white',
                              fontWeight: '600'
                            }}>
                              {note.status}
                            </span>
                          </div>
                        )}
                      </div>

                      {/* Middle: Note content preview */}
                      <div style={{
                        flex: '2 1 250px',
                        fontSize: '12px',
                        color: 'var(--ifm-color-emphasis-800)',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        display: '-webkit-box',
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: 'vertical',
                        lineHeight: '1.3'
                      }}>
                        {note.content}
                      </div>

                      {/* Right: Action buttons */}
                      <div style={{ display: 'flex', gap: '0.35rem', marginLeft: 'auto', flexWrap: 'wrap' }}>
                        {note.status === 'pending' && (
                          <>
                            <button
                              onClick={() => handleModerateNote(note.id, 'approve')}
                              disabled={moderating}
                              className="btn-moderation btn-approve"
                              style={{ padding: '4px 8px', fontSize: '11px' }}
                            >
                              ✓
                            </button>
                            <button
                              onClick={() => {
                                const reason = prompt('Rejection reason:');
                                if (reason) {
                                  handleModerateNote(note.id, 'reject', reason);
                                }
                              }}
                              disabled={moderating}
                              className="btn-moderation btn-reject"
                              style={{ padding: '4px 8px', fontSize: '11px' }}
                            >
                              ✗
                            </button>
                          </>
                        )}
                        <button
                          onClick={() => handleDeleteNote(note.id)}
                          disabled={deleting}
                          className="btn-moderation btn-delete"
                          style={{ padding: '4px 8px', fontSize: '11px' }}
                        >
                          🗑️
                        </button>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </>
      )}
    </div>
  );
};
